import { useEffect, useMemo, useState } from 'react'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  type Child,
  type Manifest,
  type Reference,
  useLazyGetAuthoringComponentQuery,
  useLazyGetAuthoringComponentRevisionQuery,
} from '../../api/authoring'
import { useTargetDocument } from '../../api/useTargetDocument'
import { generatedRouteProblems, type RouteMember } from '../../model/generatedRoute'
import { isLinked } from '../../model/link'
import { fieldsSchema, typeOf } from '../../model/manifest'
import type { WebsiteRoute } from '../../model/website'

/**
 * A generated route's unserved records, as a chip on its row.
 *
 * It reads what delivery reads: the source Collection and the page
 * pattern at the revisions the Website's edges resolve to, then each
 * member at the revision its placement does. That is one read per member,
 * the cost delivery pays per request, paid here once while the routing
 * editor is open. Nothing is drawn while a read is outstanding or when
 * every member is served.
 */
export function GeneratedRouteReport({
  tenant,
  manifest,
  references,
  route,
}: {
  tenant: string
  manifest: Manifest | undefined
  references: Reference[]
  route: WebsiteRoute
}) {
  const edge = (id: string | undefined, name: string) =>
    references.find((reference) => reference.edgeId === id && reference.name === name)
  const source = edge(route.source, 'routeSource')
  const pattern = edge(route.pattern, 'routePattern')
  const collection = useTargetDocument(tenant, source?.id, source?.revision)
  const declaration = useTargetDocument(tenant, pattern?.id, pattern?.revision)
  const members = useMembers(tenant, collection.data?.children, collection.data?.revision)

  const input = declaration.data?.fields?.input as { accepts?: string } | undefined
  const inputType = input?.accepts
  const problems = useMemo(() => {
    if (!source || !pattern) return ['The route names a source or pattern edge this Website does not hold, so it serves nothing.']
    if (!inputType || !members) return []
    return generatedRouteProblems({
      inputType,
      key: typeOf(manifest, inputType)?.key,
      fields: fieldsSchema(manifest, inputType),
      members,
      filters: (route.menuFilters ?? []).filter((filter) => route.menus?.includes(filter.tag)),
    })
  }, [source, pattern, inputType, members, manifest, route])

  if (problems.length === 0) return null
  // Focusable, and described by its tooltip while open, so the problems
  // are read by a keyboard as well as a pointer: the chip alone says only
  // how many there are.
  return (
    <Tooltip describeChild title={<>{problems.map((problem) => <div key={problem}>{problem}</div>)}</>}>
      <Chip
        size="small"
        color="warning"
        icon={<WarningAmberIcon />}
        label={problems.length === 1 ? '1 problem' : `${problems.length} problems`}
        tabIndex={0}
      />
    </Tooltip>
  )
}

/**
 * Every linked member of a source, read at the revision its placement
 * resolves to. Undefined until all of them have answered.
 *
 * Re-read whenever the Collection's revision moves, which is when its
 * membership can have changed. A member edited meanwhile is re-read on
 * the next open, which is soon enough for a report.
 */
function useMembers(tenant: string, children: Child[] | undefined, revision: number | undefined) {
  const [head] = useLazyGetAuthoringComponentQuery()
  const [at] = useLazyGetAuthoringComponentRevisionQuery()
  const [members, setMembers] = useState<RouteMember[] | undefined>()

  useEffect(() => {
    if (!children) return
    let current = true
    Promise.all(children.filter(isLinked).map(async (child) => {
      const read = child.revision === undefined
        ? head({ tenant, id: child.id }, true)
        : at({ tenant, id: child.id, revision: child.revision }, true)
      const document = await read.unwrap()
      return { id: document.id, type: document.type, label: document.label, fields: document.fields }
    }))
      .then((read) => { if (current) setMembers(read) })
      .catch(() => { if (current) setMembers(undefined) })
    return () => { current = false }
    // The revision stands for the children: a new array each render
    // would re-read every member on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, revision, head, at])

  return members
}
